import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'components/common/Button';
import { AuthHero } from 'components/auth/AuthHero';
import { AuthService } from 'services/auth/auth.service';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    let mounted = true;
    void AuthService.hasRecoverySession().then(hasSession => {
      if (mounted) setReady(hasSession);
    }).catch(() => {
      if (mounted) setReady(false);
    });
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await AuthService.updatePassword(password);
      setUpdated(true);
    } catch (resetError: any) {
      setError(resetError?.message || 'Unable to update your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-start bg-[#f7f8f5] px-4 py-4 lg:items-center lg:justify-center lg:px-0 lg:py-0">
      <div className="grid w-full items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(400px,0.85fr)] lg:items-center lg:gap-0">
        <AuthHero />
        <div className="w-full max-w-sm justify-self-center lg:mr-8">
          <div className="mb-8 text-center"><h1 className="text-3xl font-bold tracking-tight text-[#17231c]">Choose a new password</h1></div>
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            {updated ? (
              <div className="space-y-3 text-center"><h2 className="text-lg font-semibold text-gray-900">Password updated</h2><p className="text-sm text-gray-500">Your password has been changed. Sign in with your new password.</p><Link to="/login" className="inline-block text-sm text-blue-600 hover:underline">Go to sign in</Link></div>
            ) : !ready ? (
              <div className="space-y-3 text-center"><h2 className="text-lg font-semibold text-gray-900">Reset link expired</h2><p className="text-sm text-gray-500">Request a new password reset link to continue.</p><Link to="/forgot-password" className="inline-block text-sm text-blue-600 hover:underline">Request a new link</Link></div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
                <div><label className="mb-1 block text-sm font-medium text-gray-700">New password</label><input type="password" required minLength={6} value={password} onChange={event => setPassword(event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                <div><label className="mb-1 block text-sm font-medium text-gray-700">Confirm new password</label><input type="password" required minLength={6} value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                <Button type="submit" loading={loading} className="w-full justify-center">Update password</Button>
              </form>
            )}
            {!updated && <p className="mt-5 text-center text-xs text-gray-500"><button type="button" onClick={() => navigate('/login')} className="text-blue-600 hover:underline">Return to sign in</button></p>}
          </div>
        </div>
      </div>
    </div>
  );
}
